import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, Plus, Layers, Filter, X, Download } from "lucide-react";
import { api } from "../../lib/api";
import BulkBookingModal from "../../components/BulkBookingModal";
import IcalImportModal from "../../components/IcalImportModal";
import { toLocalIso } from "../../lib/dateUtils";

const TZ = "Atlantic/Madeira"; // Funchal timezone (same UTC as Europe/Lisbon year-round)
const STATUS_LABEL = {
  pending: "Pendente", confirmed: "Confirmado", in_progress: "Em curso",
  completed: "Concluído", cancelled: "Cancelado",
};
const STATUS_OPTIONS = ["pending", "confirmed", "in_progress", "completed", "cancelled"];
const APT_TYPES = ["T0", "T1", "T2", "T3", "T4", "T5", "VS", "VM", "VL", "HOTEL"];
const EXTRAS_ICONS = {
  lavandaria: "🧺", consumiveis: "🧻", limpeza_profunda: "🧼", churrasqueira: "🔥", relva: "🌿", manutencao: "🔧",
};

function todayInFunchal() {
  // Derive "today" using the Madeira timezone so the calendar highlights the right day
  // even when the browser runs in a different TZ.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const g = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${g.year}-${g.month}-${g.day}`;
}

function FunchalClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const dateStr = new Intl.DateTimeFormat("pt-PT", {
    timeZone: TZ, weekday: "long", day: "2-digit", month: "long", year: "numeric",
  }).format(now);
  const timeStr = new Intl.DateTimeFormat("pt-PT", {
    timeZone: TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(now);
  return (
    <div className="text-right" data-testid="funchal-clock">
      <div className="eyebrow text-[#d4af37]">Funchal · Madeira, PT</div>
      <div className="font-mono tabular-nums text-2xl mt-1">{timeStr}</div>
      <div className="text-xs text-[#a0b2af] capitalize">{dateStr}</div>
    </div>
  );
}

export default function AdminCalendar() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedIso, setSelectedIso] = useState(todayInFunchal);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [icalOpen, setIcalOpen] = useState(false);
  const [filters, setFilters] = useState({
    apartment_id: "", apt_type: "", status: "",
  });
  const [view, setView] = useState("month"); // "month" | "week" | "list"

  const reload = () => {
    const y = cursor.getFullYear(); const m = cursor.getMonth();
    let from = toLocalIso(new Date(y, m, 1));
    let to = toLocalIso(new Date(y, m + 1, 0));
    if (view === "week" && selectedIso) {
      const d = new Date(`${selectedIso}T12:00:00`);
      const dow = (d.getDay() + 6) % 7;
      const monday = new Date(d); monday.setDate(d.getDate() - dow);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      from = toLocalIso(monday);
      to = toLocalIso(sunday);
    }
    api.get("/bookings", { params: { from_date: from, to_date: to } })
       .then(({ data }) => setBookings(data.items));
  };

  useEffect(() => {
    api.get("/apartments").then(({ data }) => setApartments(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cursor, view, selectedIso]);

  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (filters.apartment_id && b.apartment_id !== filters.apartment_id) return false;
      if (filters.apt_type && b.apt_type !== filters.apt_type) return false;
      if (filters.status && b.status !== filters.status) return false;
      return true;
    });
  }, [bookings, filters]);

  const grid = useMemo(() => buildMonth(cursor), [cursor]);
  const todayIso = todayInFunchal();

  function byDate(dateStr) {
    return filteredBookings.filter(b => b.scheduled_date === dateStr);
  }

  const dayBookings = useMemo(() => {
    if (!selectedIso) return [];
    return byDate(selectedIso).slice().sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIso, filteredBookings]);

  const selectedLabel = selectedIso
    ? new Intl.DateTimeFormat("pt-PT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
        .format(new Date(`${selectedIso}T12:00:00`))
    : "";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow mb-3">Planeamento</div>
          <h1 className="font-display text-5xl">Agenda de limpezas</h1>
          <p className="text-sm text-[#a0b2af] mt-2">
            Calendário, lista semanal/mensal, filtros e agendamento múltiplo.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <FunchalClock />
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={() => navigate("/admin/reservas/nova")}
                    className="btn-ghost-gold px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    data-testid="agenda-new-single">
              <Plus size={14} /> Novo agendamento
            </button>
            <button onClick={() => setBulkOpen(true)}
                    className="btn-gold px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    data-testid="agenda-new-bulk">
              <Layers size={14} /> Agendamento múltiplo
            </button>
            <button onClick={() => setIcalOpen(true)}
                    className="btn-ghost-gold px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    data-testid="agenda-import-ical"
                    title="Booking.com · Airbnb · Vrbo · qualquer .ics">
              <Download size={14} /> Importar OTA
            </button>
          </div>
        </div>
      </div>

      {/* Filters + view toggle */}
      <FiltersBar filters={filters} setFilters={setFilters}
                  apartments={apartments} view={view} setView={setView} />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
                  className="btn-ghost-gold px-3 py-1.5 text-sm" data-testid="cal-prev">←</button>
          <div className="font-display text-2xl min-w-[12rem] text-center capitalize">
            {cursor.toLocaleDateString("pt-PT", { month: "long", year: "numeric" })}
          </div>
          <button onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
                  className="btn-ghost-gold px-3 py-1.5 text-sm" data-testid="cal-next">→</button>
        </div>
        <button
          onClick={() => {
            const d = new Date();
            setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
            setSelectedIso(todayInFunchal());
          }}
          className="btn-ghost-gold px-3 py-1.5 text-xs uppercase tracking-widest"
          data-testid="cal-today">Hoje</button>
      </div>

      {view === "list" ? (
        <ListView bookings={filteredBookings} apartments={apartments} />
      ) : (
        <div className="grid grid-cols-7 gap-px bg-[#1a4640] border border-[#1a4640]">
        {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map(d => (
          <div key={d} className="bg-[#041f1b] text-[#a0b2af] text-xs uppercase tracking-widest p-3 text-center">{d}</div>
        ))}
        {grid.map((cell, idx) => {
          const isToday = cell.iso === todayIso;
          const isSelected = cell.iso === selectedIso;
          const evCount = cell.inMonth ? byDate(cell.iso).length : 0;
          return (
            <button
              type="button"
              key={idx}
              disabled={!cell.inMonth}
              onClick={() => cell.inMonth && setSelectedIso(cell.iso)}
              className={`text-left min-h-[110px] p-2 transition-colors ${
                isSelected ? "bg-[#0a3a33] border border-[#d4af37]"
                : isToday ? "bg-[#062c27] ring-1 ring-[#d4af37]/60"
                : "bg-[#062c27] hover:bg-[#0a3a33]"
              } ${!cell.inMonth ? "opacity-40 cursor-default" : "cursor-pointer"}`}
              data-testid={cell.iso ? `cal-day-${cell.iso}` : undefined}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs ${isToday ? "text-[#d4af37] font-semibold" : "text-[#a0b2af]"}`}>{cell.day}</div>
                {evCount > 0 && <div className="text-[10px] text-[#d4af37] font-mono">{evCount}</div>}
              </div>
              <div className="space-y-1">
                {cell.inMonth && byDate(cell.iso).slice(0, 3).map(b => (
                  <div key={b.id} className={`text-[11px] px-2 py-1 rounded-sm truncate ev-${mapStatus(b.status)}`}
                       title={`${b.scheduled_time} ${b.apt_type}`} data-testid={`cal-ev-${b.id}`}>
                    {b.scheduled_time} · {b.apt_type}
                  </div>
                ))}
                {cell.inMonth && evCount > 3 && (
                  <div className="text-[10px] text-[#a0b2af]">+{evCount - 3} mais</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      )}

      <div className="flex flex-wrap gap-6 text-xs">
        <Legend cls="confirmed" label="Confirmado" />
        <Legend cls="pending" label="Pendente" />
        <Legend cls="completed" label="Concluído" />
        <Legend cls="blocked" label="Cancelado/Bloqueado" />
      </div>

      {/* --- DAY DETAIL LIST --- */}
      <section className="lux-card p-6 space-y-4" data-testid="day-detail-panel">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Limpezas do dia</div>
            <h2 className="font-display text-3xl capitalize">{selectedLabel || "Seleccione um dia"}</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-[#a0b2af]">
              {dayBookings.length === 0 ? "Sem limpezas"
                : `${dayBookings.length} ${dayBookings.length === 1 ? "limpeza" : "limpezas"}`}
            </div>
            {selectedIso && dayBookings.length > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/admin/folha-do-dia/${selectedIso}`)}
                className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                data-testid="print-day-sheet"
                title="Abre a folha do dia (PDF A4) com botão de voltar"
              >
                <Printer size={14} />
                Imprimir folha do dia
              </button>
            )}
          </div>
        </div>

        {dayBookings.length === 0 ? (
          <div className="text-[#a0b2af] text-sm py-6 text-center" data-testid="day-empty">
            Não existem limpezas agendadas para este dia.
          </div>
        ) : (
          <div className="divide-y divide-[#1a4640]" data-testid="day-list">
            {dayBookings.map(b => (
              <div key={b.id} className="py-4 grid md:grid-cols-12 gap-3 items-center" data-testid={`day-item-${b.id}`}>
                <div className="md:col-span-2">
                  <div className="font-mono text-xl text-[#d4af37]">{b.scheduled_time}</div>
                  <div className="text-[10px] text-[#a0b2af] uppercase tracking-widest">
                    {Math.floor(b.duration / 60)}h{b.duration % 60 ? ` ${b.duration % 60}m` : ""}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="eyebrow">{b.apt_type}</div>
                  {b.apartment_name && <div className="text-xs text-[#fdfbf7] mt-1 truncate">{b.apartment_name}</div>}
                </div>
                <div className="md:col-span-3">
                  <div className="text-sm text-[#fdfbf7]">{b.client_name}</div>
                  {b.notes && <div className="text-[11px] text-[#a0b2af] truncate">{b.notes}</div>}
                </div>
                <div className="md:col-span-3 flex flex-wrap gap-1">
                  {b.extras.length === 0
                    ? <span className="text-xs text-[#a0b2af]">sem extras</span>
                    : b.extras.map(eid => (
                      <span key={eid} className="text-[11px] bg-[#041f1b] border border-[#1a4640] px-2 py-0.5 rounded-sm">
                        {EXTRAS_ICONS[eid] || "✦"} {eid}
                      </span>
                    ))}
                </div>
                <div className="md:col-span-2 text-right">
                  <div className="font-display text-xl text-[#d4af37]">{b.total.toFixed(2)}€</div>
                  <div className={`inline-block text-[10px] uppercase tracking-widest mt-1 px-2 py-0.5 rounded-sm ev-${mapStatus(b.status)}`}>
                    {STATUS_LABEL[b.status] || b.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <BulkBookingModal open={bulkOpen}
                        apartmentId={filters.apartment_id || null}
                        onClose={() => setBulkOpen(false)}
                        onSuccess={() => { setBulkOpen(false); reload(); }} />

      <IcalImportModal open={icalOpen}
                       onClose={() => setIcalOpen(false)}
                       onSuccess={() => { setIcalOpen(false); reload(); }} />
    </div>
  );
}

function FiltersBar({ filters, setFilters, apartments, view, setView }) {
  const hasFilters = filters.apartment_id || filters.apt_type || filters.status;
  return (
    <div className="lux-card p-3 flex flex-wrap items-center gap-2" data-testid="agenda-filters">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#a0b2af]">
        <Filter size={12} /> Filtros:
      </div>
      <select value={filters.apartment_id}
              onChange={e => setFilters(f => ({ ...f, apartment_id: e.target.value }))}
              className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-xs outline-none"
              data-testid="agenda-filter-apartment">
        <option value="">Todos os alojamentos</option>
        {apartments.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <select value={filters.apt_type}
              onChange={e => setFilters(f => ({ ...f, apt_type: e.target.value }))}
              className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-xs outline-none"
              data-testid="agenda-filter-type">
        <option value="">Todas as tipologias</option>
        {APT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <select value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-xs outline-none"
              data-testid="agenda-filter-status">
        <option value="">Todos os estados</option>
        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
      </select>
      {hasFilters && (
        <button onClick={() => setFilters({ apartment_id: "", apt_type: "", status: "" })}
                className="text-[#a0b2af] hover:text-[#fca5a5] inline-flex items-center gap-1 text-xs px-2 py-1.5"
                data-testid="agenda-filter-clear">
          <X size={12} /> Limpar
        </button>
      )}
      <div className="ml-auto inline-flex border border-[#1a4640] rounded-sm overflow-hidden">
        {[
          { k: "month", label: "Mês" },
          { k: "list", label: "Lista" },
        ].map(opt => (
          <button key={opt.k}
                  onClick={() => setView(opt.k)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                    view === opt.k ? "bg-[#d4af37] text-[#041f1b]" : "text-[#a0b2af] hover:text-white"
                  }`}
                  data-testid={`agenda-view-${opt.k}`}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ListView({ bookings, apartments }) {
  const aptMap = Object.fromEntries(apartments.map(a => [a.id, a.name]));
  const sorted = [...bookings].sort(
    (a, b) => `${a.scheduled_date}${a.scheduled_time}`.localeCompare(`${b.scheduled_date}${b.scheduled_time}`),
  );
  if (sorted.length === 0) {
    return (
      <div className="lux-card p-12 text-center text-sm text-[#a0b2af]" data-testid="agenda-list-empty">
        Sem agendamentos no período/filtros selecionados.
      </div>
    );
  }
  // Group by date
  const byDate = sorted.reduce((acc, b) => {
    (acc[b.scheduled_date] = acc[b.scheduled_date] || []).push(b);
    return acc;
  }, {});
  return (
    <div className="space-y-4" data-testid="agenda-list">
      {Object.entries(byDate).map(([date, items]) => (
        <div key={date} className="lux-card p-4">
          <div className="eyebrow text-[#d4af37] mb-3">
            {new Intl.DateTimeFormat("pt-PT", { weekday: "long", day: "2-digit", month: "long" })
              .format(new Date(`${date}T12:00:00`))}
            <span className="ml-2 text-[#a0b2af]">· {items.length}</span>
          </div>
          <div className="divide-y divide-[#1a4640]">
            {items.map(b => (
              <div key={b.id}
                   className={`py-2 grid grid-cols-1 md:grid-cols-[80px_80px_1fr_120px] gap-2 items-baseline ${b.same_day_turnover ? "bg-[#ef4444]/5 -mx-2 px-2 rounded-sm" : ""}`}
                   data-testid={`agenda-list-item-${b.id}`}>
                <div className="font-mono text-sm text-[#d4af37]">{b.scheduled_time}</div>
                <div className="text-xs uppercase tracking-widest text-[#a0b2af]">{b.apt_type}</div>
                <div className="text-sm">
                  {aptMap[b.apartment_id] || b.apartment_name || b.client_name || "—"}
                  {b.same_day_turnover && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#ef4444]/15 border border-[#ef4444]/40 text-[10px] uppercase tracking-widest text-[#fca5a5] align-middle"
                          data-testid={`agenda-turnover-${b.id}`}
                          title={`Concluir até ${b.must_finish_by || "14:00"} — próximo hóspede chega hoje`}>
                      🔥 Turnover {b.must_finish_by ? `· até ${b.must_finish_by}` : ""}
                    </span>
                  )}
                  {b.notes && <div className="text-[11px] text-[#a0b2af] truncate">{b.notes}</div>}
                </div>
                <div className={`text-[10px] uppercase tracking-widest text-right md:text-left ev-${mapStatus(b.status)} px-2 py-0.5 rounded-sm inline-block`}>
                  {STATUS_LABEL[b.status] || b.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Legend({ cls, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-4 h-4 rounded-sm ev-${cls}`} />
      <span className="text-[#a0b2af]">{label}</span>
    </div>
  );
}

function buildMonth(cursor) {
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const firstDay = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const grid = [];
  const prevDays = new Date(y, m, 0).getDate();
  for (let i = 0; i < firstDay; i++) {
    grid.push({ day: prevDays - firstDay + 1 + i, inMonth: false, iso: null });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = toLocalIso(new Date(y, m, d));
    grid.push({ day: d, inMonth: true, iso });
  }
  while (grid.length % 7 !== 0) grid.push({ day: grid.length - (firstDay + daysInMonth) + 1, inMonth: false, iso: null });
  return grid;
}

function mapStatus(s) {
  if (s === "confirmed" || s === "in_progress") return "confirmed";
  if (s === "completed") return "completed";
  if (s === "cancelled") return "blocked";
  return "pending";
}
